import React, { useMemo, useState } from "react";
import { Button, Dropdown, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt, faShare } from "@fortawesome/free-solid-svg-icons";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
    handleSocialShare,
    redirectURL,
} from "../assets/functions.js";
import { getEventShareUrls } from "../utils/eventShareUrls.js";

export const EventActionLinks = ({ dataTable, notyf, shareUrl, className = "mt-3", shareButtonExtraItems = [] }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareType, setShareType] = useState("copy");
    const [selectedShareLinkKey, setSelectedShareLinkKey] = useState(null);
    const eventShareUrls = getEventShareUrls(dataTable);
    const credentialBaseUrl = eventShareUrls.credentialLogin;

    const isEventLive = dataTable.status === 2;
    const isRegistrationOpen = !!dataTable.json?.form?.status;
    const hasNetworking = !!dataTable.json?.networking?.status;
    const hasCredentialAccess = !!dataTable.json?.credentialAccess?.status;

    const links = [
        {
            key: "event",
            section: "Networking",
            label: "Acessar evento",
            enabled: hasNetworking && isEventLive,
            visible: hasNetworking,
            disabledLabel: !hasNetworking
                ? null
                : "Acessar evento",
            shareUrl: eventShareUrls.networking,
            onClick: () => redirectURL(true, eventShareUrls.networking),
        },
        {
            key: "credential-login",
            section: "Credenciamento",
            label: "Acessar evento",
            enabled: hasCredentialAccess && isEventLive,
            visible: hasCredentialAccess,
            disabledLabel: !hasCredentialAccess
                ? null
                : "Acessar evento",
            onClick: () =>
                redirectURL(
                    true,
                    credentialBaseUrl,
                ),
            shareUrl: credentialBaseUrl,
        },
        {
            key: "credential-register",
            section: "Credenciamento",
            label: "Formulário de registro",
            enabled: hasCredentialAccess && isRegistrationOpen,
            visible: hasCredentialAccess,
            disabledLabel: !hasCredentialAccess
                ? null
                : "Formulário de registro (disponível quando as inscrições estiverem abertas)",
            onClick: () => redirectURL(true, eventShareUrls.credentialRegistration),
            shareUrl: eventShareUrls.credentialRegistration,
        },
    ];

    const visibleLinks = links.filter((link) => link.visible);
    const sections = visibleLinks.reduce((acc, link) => {
        if (!acc[link.section]) {
            acc[link.section] = [];
        }

        acc[link.section].push(link);
        return acc;
    }, {});
    const sectionEntries = Object.entries(sections);
    const shareableLinks = visibleLinks.filter((link) => link.enabled && link.shareUrl);
    const shareGroups = useMemo(() => {
        const sectionOrder = ["Networking", "Credenciamento"];

        return sectionOrder
            .map((section) => ({
                section,
                links: shareableLinks.filter((link) => link.section === section),
            }))
            .filter((group) => group.links.length > 0);
    }, [shareableLinks]);
    const hasShareTargets = shareGroups.length > 0;

    const getDefaultShareLinkKey = () => shareGroups[0]?.links[0]?.key || null;

    const openShareModal = (type) => {
        setShareType(type);
        setSelectedShareLinkKey(getDefaultShareLinkKey());
        setShowShareModal(true);
    };

    const selectedShareLink = shareGroups
        .flatMap((group) => group.links)
        .find((link) => link.key === selectedShareLinkKey);

    const buildShareMessage = () => {
        if (!selectedShareLink) {
            return "";
        }

        return `${dataTable.title} - ${selectedShareLink.shareUrl}`;
    };

    const canSubmitShare = hasShareTargets && !!selectedShareLink;

    const handleShareSubmit = async () => {
        if (!canSubmitShare) {
            return;
        }

        const shareMessage = buildShareMessage();

        if (shareType === "copy") {
            await navigator.clipboard.writeText(shareMessage);
            notyf.open({
                type: "success",
                message: "Link copiado para a área de transferência",
                ripple: true,
                dismissible: true,
            });
        }

        if (shareType === "whatsapp") {
            handleSocialShare(
                "whatsapp",
                `https://wa.me/?text=${encodeURIComponent(shareMessage)}`,
            );
        }

        setShowShareModal(false);
    };

    return (
        <div className={`d-flex ${className}`.trim()}>
            <Dropdown className="me-2">
                <Dropdown.Toggle
                    variant={"primary"}
                    className="btn btn-sm btn-primary"
                    id="share-actions-dropdown"
                >
                    <FontAwesomeIcon icon={faShare} className="me-1" />
                    Compartilhar
                </Dropdown.Toggle>

                <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }}>
                    {shareButtonExtraItems.map((item) => (
                        <Dropdown.Item key={item.key} onClick={item.onClick}>
                            {item.icon && <FontAwesomeIcon icon={item.icon} className="me-2" />}
                            {item.label}
                        </Dropdown.Item>
                    ))}
                    {shareButtonExtraItems.length > 0 && <Dropdown.Divider />}
                    <Dropdown.Item
                        disabled={!hasShareTargets}
                        onClick={() => openShareModal("copy")}
                    >
                        <FontAwesomeIcon icon={faCopy} className="me-2" />
                        Compartilhar link
                    </Dropdown.Item>
                    <Dropdown.Item
                        disabled={!hasShareTargets}
                        onClick={() => openShareModal("whatsapp")}
                    >
                        <FontAwesomeIcon icon={faWhatsapp} className="me-2" />
                        Compartilhar via whatsapp
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
            <Dropdown show={showDropdown} onToggle={setShowDropdown}>
                <Dropdown.Toggle
                    variant={"primary"}
                    className="btn-sm"
                    id="links-dropdown-status"
                >
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                    Links
                </Dropdown.Toggle>
                <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }}>
                    {visibleLinks.length === 0 && (
                        <Dropdown.Item disabled>
                            Nenhum link disponível
                        </Dropdown.Item>
                    )}
                    {sectionEntries.map(([section, sectionLinks], sectionIndex) => (
                        <div key={section}>
                            <h6 className="dropdown-header">{section}</h6>
                            {sectionLinks.map((link) => (
                                <Dropdown.Item
                                    key={link.key}
                                    disabled={!link.enabled}
                                    onClick={link.enabled ? link.onClick : undefined}
                                >
                                    {link.enabled ? link.label : link.disabledLabel}
                                </Dropdown.Item>
                            ))}
                            {sectionIndex < sectionEntries.length - 1 && <Dropdown.Divider />}
                        </div>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
            <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Compartilhar link</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {shareGroups.map((group) => (
                        <div key={group.section} className="mb-3">
                            <div className="fw-bold mb-2">{group.section}</div>
                            {group.links.map((link) => (
                                <Form.Check
                                    key={link.key}
                                    type="radio"
                                    name="share-platform"
                                    id={`share-platform-${link.key}`}
                                    label={link.label}
                                    checked={selectedShareLinkKey === link.key}
                                    onChange={() => setSelectedShareLinkKey(link.key)}
                                    className="mb-2"
                                />
                            ))}
                        </div>
                    ))}
                    {!hasShareTargets && (
                        <div className="text-muted">Nenhuma plataforma disponível para compartilhamento.</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowShareModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleShareSubmit} disabled={!canSubmitShare}>
                        {shareType === "copy" ? "Copiar link" : "Compartilhar"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
